/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/shared/InlineEditableField.tsx
 * PURPOSE: Inline edit — click value, amber outline while editing; immediate save on blur or Enter, or deferred commit via section controls; Escape cancels
 * DEPENDENCIES: react, ../../utils/crmFormatters (dates), ../../utils/formatCurrency
 * PUBLIC_API: InlineEditableField, InlineEditableFieldProps, InlineEditableFieldOption
 * SIDE_EFFECTS: Invokes onSave; focuses input when editing
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 348/400
 * RECENT: 2026-04-30 [2.2.740] **`persistence`**, **`onDeferredCommit`**, **`validateDraft`**, **`linkView`** — CHANGELOG [2.2.740]. [2.2.729] Red border ring when **`error`** set; amber ring when editing — **`CHANGELOG` [2.2.729]**
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { formatCrmDate } from '../../utils/crmFormatters';
import { formatCrmCurrency } from '../../utils/formatCurrency';

export type InlineEditableFieldOption = { value: string; label: string };

export type InlineEditableFieldProps = {
  label: string;
  value: string | number;
  /** Instruction 10 name; `type` is accepted as an alias. */
  fieldType?: 'text' | 'number' | 'date' | 'select' | 'textarea';
  /** @deprecated use `fieldType` */
  type?: 'text' | 'number' | 'date' | 'select' | 'textarea';
  options?: InlineEditableFieldOption[];
  onSave: (value: string | number) => void | Promise<void>;
  readOnly?: boolean;
  formatDisplay?: (value: string | number) => string;
  /** @deprecated use `formatDisplay` — built-in formatters when set without formatDisplay */
  format?: 'currency' | 'percent' | 'date' | 'text';
  required?: boolean;
  validationMessage?: string;
  onCancel?: () => void;
  /**
   * `immediate` — blur/Enter calls `onSave` (default).
   * `deferred` — blur/Enter validates and updates display via `onDeferredCommit` only; parent batches `onSave` (e.g. section Save).
   */
  persistence?: 'immediate' | 'deferred';
  /** Required when `persistence` is `deferred`; receives validated payload after blur/Enter. */
  onDeferredCommit?: (value: string | number) => void;
  /** Optional extra validation; return error message or null when valid. */
  validateDraft?: (draft: string) => string | null;
  /** Read-only presentation: `mailto:` / `tel:` / external URL link (opens new tab for url). */
  linkView?: 'none' | 'email' | 'tel' | 'url';
};

function toDraft(value: string | number, fieldType: NonNullable<InlineEditableFieldProps['fieldType']>): string {
  if (fieldType === 'date' && typeof value === 'string') {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    return m ? m[1]! : value.slice(0, 10);
  }
  return String(value);
}

function defaultFormatDisplay(
  value: string | number,
  format: InlineEditableFieldProps['format'],
  fieldType: NonNullable<InlineEditableFieldProps['fieldType']>
): string {
  if (value === '') return '—';
  if (format === 'currency') {
    const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
    return Number.isFinite(n) ? formatCrmCurrency(n) : String(value);
  }
  if (format === 'percent') {
    const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
    return Number.isFinite(n) ? `${n}%` : String(value);
  }
  if (format === 'date' || fieldType === 'date') {
    if (typeof value === 'string') return formatCrmDate(value);
  }
  return String(value);
}

const AMBER_RING =
  'ring-2 ring-[color:var(--color-warning,#d97706)] border border-[color:var(--color-warning,#d97706)]';
/** Validation or save failure — distinct from unsaved (amber) edit state */
const ERROR_RING = 'ring-2 ring-red-500 border border-red-500';

export function InlineEditableField({
  label,
  value,
  fieldType: fieldTypeProp,
  type: typeAlias,
  options = [],
  onSave,
  readOnly,
  formatDisplay,
  format = 'text',
  required,
  validationMessage,
  onCancel,
  persistence = 'immediate',
  onDeferredCommit,
  validateDraft,
  linkView = 'none',
}: InlineEditableFieldProps) {
  const fieldType = fieldTypeProp ?? typeAlias ?? 'text';
  const id = useId();
  const inputId = `${id}-input`;
  const errorId = `${id}-err`;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => toDraft(value, fieldType));
  const [committed, setCommitted] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  useEffect(() => {
    if (!editing) {
      setDraft(toDraft(value, fieldType));
      setCommitted(value);
    }
  }, [value, fieldType, editing]);

  const viewText = useMemo(() => {
    if (formatDisplay) return formatDisplay(value);
    if (fieldType === 'select' && options.length) {
      const opt = options.find((o) => String(o.value) === String(value));
      if (opt) return opt.label;
    }
    return defaultFormatDisplay(value, format, fieldType);
  }, [value, formatDisplay, format, fieldType, options]);

  const startEdit = useCallback(() => {
    if (readOnly) return;
    let d = toDraft(value, fieldType);
    if (fieldType === 'select' && options.length && !options.some((o) => o.value === d)) {
      d = options[0]!.value;
    }
    setDraft(d);
    setError(null);
    setEditing(true);
  }, [readOnly, value, fieldType, options]);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const cancelEdit = useCallback(() => {
    setDraft(toDraft(committed, fieldType));
    setError(null);
    setEditing(false);
    onCancel?.();
  }, [committed, fieldType, onCancel]);

  const commitValue = useCallback(async () => {
    if (saving) return;
    if (required && String(draft).trim() === '') {
      setError(validationMessage ?? 'This field is required.');
      return;
    }
    if (validateDraft) {
      const v = validateDraft(String(draft));
      if (v) {
        setError(v);
        return;
      }
    }
    setError(null);
    setSaving(true);
    let payload: string | number = draft;
    if (fieldType === 'number') {
      const n = Number(String(draft).replace(/,/g, ''));
      if (!Number.isFinite(n)) {
        setError('Enter a valid number.');
        setSaving(false);
        return;
      }
      payload = n;
    }
    try {
      if (persistence === 'deferred') {
        onDeferredCommit?.(payload);
        setCommitted(typeof payload === 'number' ? payload : draft);
        setEditing(false);
      } else {
        await Promise.resolve(onSave(payload));
        setCommitted(typeof payload === 'number' ? payload : draft);
        setEditing(false);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }, [
    draft,
    fieldType,
    onSave,
    onDeferredCommit,
    persistence,
    required,
    saving,
    validationMessage,
    validateDraft,
  ]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      } else if (e.key === 'Enter' && fieldType !== 'textarea') {
        e.preventDefault();
        void commitValue();
      }
    },
    [cancelEdit, commitValue, fieldType]
  );

  const inputBase =
    'min-w-0 w-full max-w-full rounded-md bg-white px-2 py-1.5 text-sm font-semibold text-gray-900 outline-none transition-shadow dark:bg-gray-800 dark:text-white';

  return (
    <div data-testid="inline-editable-field">
      {label ? (
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
      ) : null}
      {!editing ? (
        <div className="mt-0.5">
          {readOnly ? (
            <span className="block text-sm font-semibold text-gray-900 dark:text-white">{viewText}</span>
          ) : linkView !== 'none' && String(value).trim() !== '' ? (
            <div className="flex flex-wrap items-center gap-2">
              {linkView === 'email' ? (
                <a
                  href={`mailto:${encodeURIComponent(String(value))}`}
                  className="text-sm font-semibold text-primary-600 hover:underline dark:text-primary-400"
                  onClick={(e) => e.stopPropagation()}
                >
                  {viewText}
                </a>
              ) : linkView === 'tel' ? (
                <a
                  href={`tel:${String(value).replace(/\s/g, '')}`}
                  className="text-sm font-semibold text-primary-600 hover:underline dark:text-primary-400"
                  onClick={(e) => e.stopPropagation()}
                >
                  {viewText}
                </a>
              ) : (
                <a
                  href={String(value)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all text-sm font-semibold text-primary-600 hover:underline dark:text-primary-400"
                  onClick={(e) => e.stopPropagation()}
                >
                  {viewText}
                </a>
              )}
              <button
                type="button"
                className="rounded text-xs font-medium text-gray-500 underline-offset-2 hover:underline dark:text-gray-400"
                aria-label={label ? `Edit ${label}` : 'Edit field'}
                onClick={startEdit}
              >
                Edit
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="block w-full max-w-full rounded text-left text-sm font-semibold text-gray-900 hover:bg-gray-50 focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-white dark:hover:bg-gray-800/80"
              aria-label={label ? `Edit ${label}` : 'Edit field'}
              onClick={startEdit}
            >
              {viewText}
            </button>
          )}
        </div>
      ) : (
        <div className="mt-1">
          {fieldType === 'textarea' ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              id={inputId}
              className={`${inputBase} ${error ? ERROR_RING : AMBER_RING} min-h-[4.5rem]`}
              rows={3}
              value={draft}
              disabled={saving}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              onBlur={() => void commitValue()}
            />
          ) : fieldType === 'select' ? (
            <select
              ref={inputRef as React.RefObject<HTMLSelectElement>}
              id={inputId}
              className={`${inputBase} ${error ? ERROR_RING : AMBER_RING}`}
              value={draft}
              disabled={saving}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={() => void commitValue()}
            >
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              id={inputId}
              className={`${inputBase} ${error ? ERROR_RING : AMBER_RING}`}
              type={fieldType === 'number' ? 'number' : fieldType === 'date' ? 'date' : 'text'}
              value={draft}
              disabled={saving}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={() => void commitValue()}
            />
          )}
        </div>
      )}
      {error ? (
        <p id={errorId} className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
